import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context.jsx';
import AppShell from './components/AppShell.jsx';
import './styles/tokens.css';
import './styles/global.css';

const LoginPage = lazy(() => import('./pages/LoginPage.jsx'));
const EngagementsPage = lazy(() => import('./pages/EngagementsPage.jsx'));
const EngagementDetailPage = lazy(() => import('./pages/EngagementDetailPage.jsx'));
const QuestionsPage = lazy(() => import('./pages/QuestionsPage.jsx'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage.jsx'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage.jsx'));
const CaseStudyEditPage = lazy(() => import('./pages/CaseStudyEditPage.jsx'));
const SettingsPage = lazy(() => import('./pages/SettingsPage.jsx'));

function Gate({ children }) {
  const { loading, session, allowed, parIdentityOk, isAdmin, identityError } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!session) return <Navigate to="/login" replace />;
  if (!allowed) {
    return (
      <UnauthorizedScreen
        identityError={identityError}
        parIdentityOk={parIdentityOk}
        isAdmin={isAdmin}
      />
    );
  }
  return children;
}

function FullScreenLoader() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      color: 'var(--text-2)',
      fontSize: 13,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    }}>
      Loading…
    </div>
  );
}

function UnauthorizedScreen({ identityError, parIdentityOk, isAdmin }) {
  // Three failure modes, in priority order:
  //   1. Server / upstream failure (≥500)            → "temporarily unavailable"
  //   2. PAR identity not found                       → "not linked to PAR"
  //   3. PAR identity OK but not in margin_admins    → "not authorized for Margin"
  const isServerError = identityError?.status >= 500;
  const isParMiss = !isServerError && !parIdentityOk;
  const isAdminMiss = !isServerError && parIdentityOk && !isAdmin;

  let heading, body;
  if (isServerError) {
    heading = 'Sign-in is temporarily unavailable.';
    body = "We couldn't reach the PAR identity service. Please try again in a minute.";
  } else if (isAdminMiss) {
    heading = 'Your PAR account isn\'t authorized for Margin yet.';
    body = 'Ask Jason to add you in console Settings → Margin admins. Once added, refresh this page.';
  } else if (isParMiss) {
    heading = "This email isn't linked to PAR.";
    body = 'Sign out and try again with the email connected to your PAR account.';
  } else {
    // Defensive: shouldn't reach here, but render something sane.
    heading = 'Sign-in failed.';
    body = 'Please try signing out and back in.';
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      padding: 32,
      textAlign: 'center',
    }}>
      <div>
        <div style={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 12 }}>
          PAR Margin
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 12, color: 'var(--text-0)' }}>
          {heading}
        </h1>
        <p style={{ color: 'var(--text-2)', fontSize: 14, maxWidth: 420, margin: '0 auto' }}>
          {body}
        </p>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<FullScreenLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<Gate><AppShell /></Gate>}>
              <Route index element={<Navigate to="/engagements" replace />} />
              <Route path="engagements" element={<EngagementsPage />} />
              <Route path="engagements/:id" element={<EngagementDetailPage />} />
              <Route path="questions" element={<QuestionsPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="case-studies" element={<CaseStudiesPage />} />
              <Route path="case-studies/:id" element={<CaseStudyEditPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  </StrictMode>
);
